import type { SiteTheme } from "../schema/index.js";
import { isRecord } from "../utils.js";
import { sanitizeCss } from "../security/index.js";
import { SIMPLE_EDITOR_DEFAULTS } from "../defaults/smart-defaults.js";

export const SITE_THEME_BUTTON_STYLES = ["solid", "soft", "outline", "ghost"] as const;
export type SiteThemeButtonStyle = (typeof SITE_THEME_BUTTON_STYLES)[number];

export const SITE_THEME_CARD_STYLES = ["flat", "outline", "shadow", "elevated"] as const;
export type SiteThemeCardStyle = (typeof SITE_THEME_CARD_STYLES)[number];

export const SITE_THEME_PRESET_IDS = [
  "modern",
  "classic",
  "bold",
  "minimal",
  "playful",
] as const;
export type SiteThemePresetId = (typeof SITE_THEME_PRESET_IDS)[number];

export interface SiteThemePreset {
  id: SiteThemePresetId;
  name: string;
  description: string;
  theme: SiteTheme;
}

export const VERTICAL_SITE_THEME_PRESET_MAP = {
  agency: "modern",
  coaching: "classic",
  dental: "modern",
  ecommerce: "bold",
  education: "modern",
  fitness: "bold",
  generic: "modern",
  health: "minimal",
  "home-services": "modern",
  legal: "classic",
  medical: "modern",
  photography: "classic",
  "real-estate": "classic",
  restaurant: "classic",
  "salon-spa": "playful",
  yoga: "minimal",
  "yoga-fitness": "minimal",
} as const satisfies Record<string, SiteThemePresetId>;

export const SITE_THEME_PRESETS: readonly SiteThemePreset[] = [
  {
    id: "modern",
    name: "Modern",
    description: "Clean SaaS defaults with crisp blues and neutral surfaces.",
    theme: {
      presetId: "modern",
      colors: {
        brand: "#1040CC",
        brandDark: "#0B2D8F",
        secondary: "#1D4ED8",
        accent: "#F05E0A",
        text: "#0F172A",
        background: "#FFFFFF",
        surface: "#F8FAFC",
        muted: "#E2E8F0",
      },
      fonts: {
        heading: { family: "Inter", weight: "700", googleFont: "Inter" },
        body: { family: "Inter", weight: "400", googleFont: "Inter" },
      },
      borderRadius: "12px",
      spacing: "4rem",
      buttonStyle: "solid",
      cardStyle: "shadow",
    },
  },
  {
    id: "classic",
    name: "Classic",
    description: "Editorial serif headings with warm surfaces and softer contrast.",
    theme: {
      presetId: "classic",
      colors: {
        brand: "#1E3A5F",
        brandDark: "#152A44",
        secondary: "#8B6914",
        accent: "#C45A2A",
        text: "#1A1A1A",
        background: "#FEFDFB",
        surface: "#F5F0EB",
        muted: "#E7DED5",
      },
      fonts: {
        heading: {
          family: "Playfair Display",
          weight: "700",
          googleFont: "Playfair Display",
        },
        body: { family: "Source Sans 3", weight: "400", googleFont: "Source Sans 3" },
      },
      borderRadius: "8px",
      spacing: "4.5rem",
      buttonStyle: "soft",
      cardStyle: "outline",
    },
  },
  {
    id: "bold",
    name: "Bold",
    description: "High-contrast campaign look with loud accents and tighter spacing.",
    theme: {
      presetId: "bold",
      colors: {
        brand: "#FF3366",
        brandDark: "#C81E4B",
        secondary: "#6C3AFF",
        accent: "#FFB800",
        text: "#FFFFFF",
        background: "#0A0A0A",
        surface: "#18181B",
        muted: "#27272A",
      },
      fonts: {
        heading: { family: "Montserrat", weight: "800", googleFont: "Montserrat" },
        body: { family: "Montserrat", weight: "500", googleFont: "Montserrat" },
      },
      borderRadius: "0",
      spacing: "3.5rem",
      buttonStyle: "solid",
      cardStyle: "elevated",
    },
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Quiet monochrome system with restrained accents and generous whitespace.",
    theme: {
      presetId: "minimal",
      colors: {
        brand: "#111111",
        brandDark: "#000000",
        secondary: "#525252",
        accent: "#0066FF",
        text: "#111111",
        background: "#FFFFFF",
        surface: "#FAFAFA",
        muted: "#E5E5E5",
      },
      fonts: {
        heading: { family: "DM Sans", weight: "700", googleFont: "DM Sans" },
        body: { family: "DM Sans", weight: "400", googleFont: "DM Sans" },
      },
      borderRadius: "16px",
      spacing: "5rem",
      buttonStyle: "outline",
      cardStyle: "flat",
    },
  },
  {
    id: "playful",
    name: "Playful",
    description: "Rounded, upbeat styling with bright contrast for expressive small-business pages.",
    theme: {
      presetId: "playful",
      colors: {
        brand: "#6366F1",
        brandDark: "#4338CA",
        secondary: "#EC4899",
        accent: "#F59E0B",
        text: "#1E1B4B",
        background: "#FFFBF5",
        surface: "#F5F3FF",
        muted: "#E9D5FF",
      },
      fonts: {
        heading: { family: "Poppins", weight: "700", googleFont: "Poppins" },
        body: { family: "Poppins", weight: "400", googleFont: "Poppins" },
      },
      borderRadius: "24px",
      spacing: "4.25rem",
      buttonStyle: "soft",
      cardStyle: "shadow",
    },
  },
] as const;

function asNonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;
}

type SiteThemeFont = NonNullable<NonNullable<SiteTheme["fonts"]>["heading"]>;

function sanitizeFont(value: unknown): SiteThemeFont | undefined {
  if (!isRecord(value)) return undefined;

  const family = asNonEmptyString(value.family);
  if (!family) return undefined;

  return {
    family,
    ...(asNonEmptyString(value.weight) ? { weight: asNonEmptyString(value.weight) } : {}),
    ...(asNonEmptyString(value.googleFont) ? { googleFont: asNonEmptyString(value.googleFont) } : {}),
  };
}

export function pruneSiteTheme(theme: SiteTheme): SiteTheme {
  const colors = theme.colors
    ? Object.fromEntries(
        Object.entries(theme.colors).filter(([, value]) => typeof value === "string" && value.trim().length > 0),
      )
    : undefined;
  const fonts = theme.fonts
    ? {
        ...(theme.fonts.heading ? { heading: theme.fonts.heading } : {}),
        ...(theme.fonts.body ? { body: theme.fonts.body } : {}),
      }
    : undefined;

  return {
    ...(theme.presetId ? { presetId: theme.presetId } : {}),
    ...(colors && Object.keys(colors).length > 0 ? { colors } : {}),
    ...(fonts && Object.keys(fonts).length > 0 ? { fonts } : {}),
    ...(theme.customCss ? { customCss: theme.customCss } : {}),
    ...(theme.borderRadius ? { borderRadius: theme.borderRadius } : {}),
    ...(theme.spacing ? { spacing: theme.spacing } : {}),
    ...(theme.buttonStyle ? { buttonStyle: theme.buttonStyle } : {}),
    ...(theme.cardStyle ? { cardStyle: theme.cardStyle } : {}),
  };
}

export function mergeSiteThemes(base: SiteTheme, override: SiteTheme): SiteTheme {
  return pruneSiteTheme({
    ...base,
    ...override,
    colors: {
      ...(base.colors ?? {}),
      ...(override.colors ?? {}),
    },
    fonts: {
      ...(base.fonts ?? {}),
      ...(override.fonts ?? {}),
    },
  });
}

function buildThemeFromInput(
  theme: Record<string, unknown>,
  presetTheme: SiteTheme | undefined,
): SiteTheme {
  return pruneSiteTheme({
    ...(presetTheme ?? {}),
    ...(isSiteThemePresetId(asNonEmptyString(theme.presetId)) ? { presetId: asNonEmptyString(theme.presetId) } : {}),
    colors: {
      ...(presetTheme?.colors ?? {}),
      ...(isRecord(theme.colors)
        ? {
            ...(asNonEmptyString(theme.colors.brand) ? { brand: asNonEmptyString(theme.colors.brand) } : {}),
            ...(asNonEmptyString(theme.colors.brandDark) ? { brandDark: asNonEmptyString(theme.colors.brandDark) } : {}),
            ...(asNonEmptyString(theme.colors.secondary) ? { secondary: asNonEmptyString(theme.colors.secondary) } : {}),
            ...(asNonEmptyString(theme.colors.accent) ? { accent: asNonEmptyString(theme.colors.accent) } : {}),
            ...(asNonEmptyString(theme.colors.text) ? { text: asNonEmptyString(theme.colors.text) } : {}),
            ...(asNonEmptyString(theme.colors.background) ? { background: asNonEmptyString(theme.colors.background) } : {}),
            ...(asNonEmptyString(theme.colors.surface) ? { surface: asNonEmptyString(theme.colors.surface) } : {}),
            ...(asNonEmptyString(theme.colors.muted) ? { muted: asNonEmptyString(theme.colors.muted) } : {}),
          }
        : {}),
    },
    fonts: {
      ...(presetTheme?.fonts ?? {}),
      ...(isRecord(theme.fonts)
        ? {
            ...(sanitizeFont(theme.fonts.heading) ? { heading: sanitizeFont(theme.fonts.heading) } : {}),
            ...(sanitizeFont(theme.fonts.body) ? { body: sanitizeFont(theme.fonts.body) } : {}),
          }
        : {}),
    },
    ...(asNonEmptyString(theme.customCss) ? { customCss: sanitizeCss(asNonEmptyString(theme.customCss)!) } : {}),
    ...(asNonEmptyString(theme.borderRadius) ? { borderRadius: asNonEmptyString(theme.borderRadius) } : {}),
    ...(asNonEmptyString(theme.spacing) ? { spacing: asNonEmptyString(theme.spacing) } : {}),
    ...(SITE_THEME_BUTTON_STYLES.includes(theme.buttonStyle as SiteThemeButtonStyle)
      ? { buttonStyle: theme.buttonStyle as SiteThemeButtonStyle }
      : {}),
    ...(SITE_THEME_CARD_STYLES.includes(theme.cardStyle as SiteThemeCardStyle)
      ? { cardStyle: theme.cardStyle as SiteThemeCardStyle }
      : {}),
  });
}

export function isSiteThemePresetId(value: string | undefined | null): value is SiteThemePresetId {
  return typeof value === "string" && SITE_THEME_PRESET_IDS.includes(value as SiteThemePresetId);
}

export function getSiteThemePreset(presetId: string | undefined | null): SiteThemePreset | null {
  if (!isSiteThemePresetId(presetId)) return null;
  return SITE_THEME_PRESETS.find((preset) => preset.id === presetId) ?? null;
}

export function getSiteThemePresetForVertical(vertical: string | undefined | null): SiteThemePreset | null {
  if (!vertical) {
    return null;
  }

  const normalized = vertical.trim().toLowerCase();
  const presetId = Object.prototype.hasOwnProperty.call(VERTICAL_SITE_THEME_PRESET_MAP, normalized)
    ? VERTICAL_SITE_THEME_PRESET_MAP[normalized as keyof typeof VERTICAL_SITE_THEME_PRESET_MAP]
    : undefined;
  return getSiteThemePreset(presetId ?? null);
}

interface BuildSimpleSiteThemeInput {
  vertical?: string | null;
  colors?: Pick<NonNullable<SiteTheme["colors"]>, "brand" | "accent" | "background" | "text">;
}

export function buildSimpleSiteTheme(input: BuildSimpleSiteThemeInput): SiteTheme {
  const preset = getSiteThemePresetForVertical(input.vertical) ?? getSiteThemePreset("modern");

  return normalizeSiteTheme({
    presetId: preset?.id,
    spacing: SIMPLE_EDITOR_DEFAULTS.sectionSpacing,
    borderRadius: SIMPLE_EDITOR_DEFAULTS.cardRadius,
    buttonStyle: "solid",
    cardStyle: "shadow",
    colors: {
      surface: SIMPLE_EDITOR_DEFAULTS.cardBackground,
      ...(input.colors ?? {}),
    },
  });
}

export function sanitizeSiteTheme(theme: unknown): SiteTheme {
  if (!isRecord(theme)) return {};

  return buildThemeFromInput(theme, undefined);
}

export function normalizeSiteTheme(theme: unknown): SiteTheme {
  if (!isRecord(theme)) return {};

  const preset = getSiteThemePreset(asNonEmptyString(theme.presetId));

  return buildThemeFromInput(theme, preset?.theme);
}

export function resolveSiteTheme(theme: SiteTheme | undefined | null): SiteTheme {
  if (!theme) return {};

  const preset = getSiteThemePreset(theme.presetId);
  if (!preset) {
    return pruneSiteTheme(theme);
  }

  return mergeSiteThemes(preset.theme, theme);
}
